import { z } from 'zod';
import type { LocaleId } from '../locales';
import { t, type Tx } from '../video-gallery/kit';

/**
 * What a generated video is made of: the user's own texts and media. Every field but the
 * headline is optional; the generator only uses scenes the content can fill (no photos, no photo
 * scenes; no quote, no review scene), so the same content gives many different videos.
 */

const line = (max: number) => z.string().trim().max(max);

export const videoContentSchema = z.object({
  /** Brand or product name. */
  brand: line(40).optional(),
  headline: line(90).min(1),
  subline: line(140).optional(),
  /** 2-5 short points: features, benefits, steps. */
  benefits: z.array(line(48)).max(5).optional(),
  /** Big numbers: "+42%" · "faster launches". */
  stats: z
    .array(z.object({ value: line(12).min(1), label: line(40) }))
    .max(4)
    .optional(),
  /** The offer: "−30%", "2 for 1", "Free trial". */
  offer: line(24).optional(),
  /** Under the offer: "This week only". */
  offerNote: line(60).optional(),
  price: line(20).optional(),
  oldPrice: line(20).optional(),
  quote: line(160).optional(),
  author: line(60).optional(),
  /** Call to action on the button. */
  cta: line(32).optional(),
  /** Site, handle or address under the button. */
  site: line(60).optional(),
  /** Image sources: uploaded files (`/files/…`), media refs or gallery photos (`gallery:<id>`). */
  photos: z.array(z.string().max(4096)).max(12).optional(),
  logo: z.string().max(4096).optional(),
});
export type VideoContent = z.infer<typeof videoContentSchema>;

/** Titles of scenes the content has no words for, in the interface language. */
export const GEN_TITLES = {
  benefits: t(
    'Why you will love it',
    'Чому вам сподобається',
    'Почему вам понравится',
    'Dlaczego to pokochasz',
    'De ce o să-ți placă',
    'Por qué te encantará',
  ),
  stats: t('In numbers', 'У цифрах', 'В цифрах', 'W liczbach', 'În cifre', 'En cifras'),
  offerNote: t(
    'Limited time',
    'Обмежений час',
    'Ограниченное время',
    'Tylko przez chwilę',
    'Timp limitat',
    'Por tiempo limitado',
  ),
  cta: t(
    'Learn more',
    'Дізнатися більше',
    'Узнать больше',
    'Dowiedz się więcej',
    'Află mai multe',
    'Saber más',
  ),
  gallery: t(
    'Have a look',
    'Подивіться',
    'Посмотрите',
    'Zobacz sam',
    'Aruncă o privire',
    'Echa un vistazo',
  ),
};

/** Sample content for the form placeholders and the first preview, in the interface language. */
export function demoVideoContent(locale: LocaleId): VideoContent {
  const tx = (v: Tx) => v[locale] ?? v.en;
  return {
    brand: 'Nova',
    headline: tx(
      t(
        'Your mornings just got better',
        'Ваші ранки стали кращими',
        'Ваши утра стали лучше',
        'Twoje poranki właśnie się poprawiły',
        'Diminețile tale tocmai s-au îmbunătățit',
        'Tus mañanas acaban de mejorar',
      ),
    ),
    subline: tx(
      t(
        'Fresh coffee delivered to your door every week.',
        'Свіжа кава до ваших дверей щотижня.',
        'Свежий кофе к вашей двери каждую неделю.',
        'Świeża kawa pod drzwi co tydzień.',
        'Cafea proaspătă la ușa ta în fiecare săptămână.',
        'Café recién tostado en tu puerta cada semana.',
      ),
    ),
    benefits: [
      tx(
        t(
          'Roasted this week',
          'Обсмажена цього тижня',
          'Обжарен на этой неделе',
          'Palona w tym tygodniu',
          'Prăjită săptămâna asta',
          'Tostado esta semana',
        ),
      ),
      tx(
        t(
          'Free delivery',
          'Безкоштовна доставка',
          'Бесплатная доставка',
          'Darmowa dostawa',
          'Livrare gratuită',
          'Envío gratis',
        ),
      ),
      tx(
        t(
          'Pause anytime',
          'Пауза будь-коли',
          'Пауза в любой момент',
          'Pauza w każdej chwili',
          'Pauză oricând',
          'Pausa cuando quieras',
        ),
      ),
    ],
    stats: [
      {
        value: '12k',
        label: tx(
          t(
            'happy subscribers',
            'задоволених підписників',
            'довольных подписчиков',
            'zadowolonych subskrybentów',
            'abonați mulțumiți',
            'suscriptores felices',
          ),
        ),
      },
      {
        value: '4.9★',
        label: tx(
          t(
            'average rating',
            'середня оцінка',
            'средняя оценка',
            'średnia ocena',
            'nota medie',
            'valoración media',
          ),
        ),
      },
    ],
    offer: '−30%',
    offerNote: tx(
      t(
        'On your first box',
        'На першу коробку',
        'На первую коробку',
        'Na pierwsze pudełko',
        'La prima cutie',
        'En tu primera caja',
      ),
    ),
    price: '$19',
    oldPrice: '$27',
    quote: tx(
      t(
        'The best coffee I have had at home.',
        'Найкраща кава, яку я пив удома.',
        'Лучший кофе, который я пил дома.',
        'Najlepsza kawa, jaką piłem w domu.',
        'Cea mai bună cafea pe care am băut-o acasă.',
        'El mejor café que he tomado en casa.',
      ),
    ),
    author: 'Anna K.',
    cta: tx(t('Order now', 'Замовити', 'Заказать', 'Zamów teraz', 'Comandă acum', 'Pedir ahora')),
    site: 'nova.coffee',
    photos: ['gallery:coffee-cup', 'gallery:coffee-beans', 'gallery:bakery', 'gallery:cake'],
  };
}
